import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, ChevronRight, ChevronLeft, Sparkles, 
  CheckCircle2, MessageCircle, Users, Pause,
  Award
} from 'lucide-react';

// Kristin Neff's three components of self-compassion
const SELF_COMPASSION_COMPONENTS = {
  mindfulness: {
    icon: <Pause className="w-5 h-5" />,
    title: 'Mindfulness',
    description: 'Acknowledging the pain without over-identifying with it',
    opposite: 'Over-identification (being swept up in negative emotions)',
    exercises: [
      'Notice the difficult feeling without trying to fix it',
      'Name the emotion: "This is anxiety" rather than "I am anxious"',
      'Observe where you feel it in your body'
    ]
  },
  commonHumanity: {
    icon: <Users className="w-5 h-5" />,
    title: 'Common Humanity',
    description: 'Recognising that suffering is part of the shared human experience',
    opposite: 'Isolation (feeling like you\'re the only one who struggles)',
    exercises: [
      'Remind yourself: "Other people feel this way too"',
      'Think of someone who might understand this feeling',
      'Consider how common this experience actually is'
    ]
  },
  selfKindness: {
    icon: <Heart className="w-5 h-5" />,
    title: 'Self-Kindness',
    description: 'Treating yourself with warmth and understanding',
    opposite: 'Self-judgment (harsh self-criticism)',
    exercises: [
      'Speak to yourself as you would a dear friend',
      'Offer yourself words of comfort',
      'Consider what you need right now'
    ]
  }
};

// Self-compassion exercise types
const EXERCISES = [
  {
    id: 'self-compassion-break',
    title: 'Self-Compassion Break',
    description: 'A quick practice for difficult moments',
    duration: '3-5 minutes',
    difficulty: 'beginner',
    steps: [
      { 
        component: 'mindfulness',
        prompt: 'Acknowledge what\'s happening',
        instruction: 'What\'s the difficult situation or feeling you\'re experiencing right now? Describe it without judgment.',
        example: 'I\'m feeling overwhelmed by work deadlines and like I can\'t cope.'
      },
      { 
        component: 'commonHumanity',
        prompt: 'Remember you\'re not alone',
        instruction: 'Remind yourself that struggling is part of being human. Write a statement acknowledging this.',
        example: 'Many people feel overwhelmed sometimes. This is a normal human experience, not a personal failing.'
      },
      { 
        component: 'selfKindness',
        prompt: 'Offer yourself kindness',
        instruction: 'What words of comfort would you offer a good friend in this situation? Offer those same words to yourself.',
        example: 'May I be kind to myself in this moment. May I give myself the compassion I need.'
      }
    ]
  },
  {
    id: 'compassionate-letter',
    title: 'Compassionate Letter',
    description: 'Write to yourself from a place of unconditional love',
    duration: '10-15 minutes',
    difficulty: 'intermediate',
    steps: [
      {
        component: 'mindfulness',
        prompt: 'Describe the struggle',
        instruction: 'Write about what\'s been difficult for you recently. Be honest about the pain.',
        example: 'I\'ve been really hard on myself about...'
      },
      {
        component: 'commonHumanity',
        prompt: 'Connect to others',
        instruction: 'Write about how this struggle connects you to others. Who else might feel this way?',
        example: 'So many people struggle with this. I\'m not alone in feeling...'
      },
      {
        component: 'selfKindness',
        prompt: 'Write with compassion',
        instruction: 'Write a letter to yourself from the perspective of an unconditionally loving friend or mentor.',
        example: 'Dear [your name], I want you to know that you are doing the best you can...'
      }
    ]
  },
  {
    id: 'inner-critic-dialogue',
    title: 'Transform Your Inner Critic',
    description: 'Change self-criticism into self-compassion',
    duration: '10 minutes',
    difficulty: 'intermediate',
    steps: [
      {
        component: 'mindfulness',
        prompt: 'Notice the inner critic',
        instruction: 'What does your inner critic typically say to you? Write its harsh words.',
        example: '"You\'re not good enough. You always mess things up."'
      },
      {
        component: 'commonHumanity',
        prompt: 'Understand its origin',
        instruction: 'Where might this voice have come from? How might it have been trying to protect you?',
        example: 'This voice might have developed to help me avoid rejection or failure...'
      },
      {
        component: 'selfKindness',
        prompt: 'Respond with compassion',
        instruction: 'Write a compassionate response to your inner critic. Thank it for trying to help, then offer a kinder perspective.',
        example: 'Thank you for trying to protect me, but I choose to speak to myself more kindly now...'
      }
    ]
  }
];

// Common self-compassion phrases
const COMPASSION_PHRASES = [
  'May I be kind to myself',
  'May I accept myself as I am',
  'May I give myself the compassion I need',
  'This is a moment of suffering',
  'Suffering is part of life',
  'May I be patient with myself',
  'I am doing the best I can',
  'May I forgive myself',
  'I am worthy of love and kindness',
  'May I be gentle with myself'
];

interface SelfCompassionExerciseProps {
  onComplete?: (data: ExerciseData) => void;
}

interface ExerciseData {
  exerciseId: string;
  responses: Record<string, string>;
  completedAt: string;
}

export function SelfCompassionExercise({ onComplete }: SelfCompassionExerciseProps) {
  const [selectedExercise, setSelectedExercise] = useState<typeof EXERCISES[0] | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [isComplete, setIsComplete] = useState(false);
  const [showPhrases, setShowPhrases] = useState(false);

  const startExercise = (exercise: typeof EXERCISES[0]) => {
    setSelectedExercise(exercise);
    setCurrentStep(0);
    setResponses({});
    setIsComplete(false);
  };

  const handleNext = () => {
    if (selectedExercise && currentStep < selectedExercise.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    if (selectedExercise) {
      const data: ExerciseData = {
        exerciseId: selectedExercise.id,
        responses,
        completedAt: new Date().toISOString()
      };
      setIsComplete(true);
      onComplete?.(data);
    }
  };

  const updateResponse = (stepIndex: number, value: string) => {
    setResponses(prev => ({
      ...prev,
      [stepIndex]: value
    }));
  };

  const resetExercise = () => {
    setSelectedExercise(null);
    setCurrentStep(0);
    setResponses({});
    setIsComplete(false);
  };

  // Exercise Selection View
  if (!selectedExercise) {
    return (
      <div className="space-y-6">
        {/* Introduction */}
        <Card className="bg-gradient-to-br from-secondary/5 to-accent/5 border-secondary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-secondary" />
              Self-Compassion Practice
            </CardTitle>
            <CardDescription>
              Self-compassion isn't self-pity or self-indulgence. It's treating yourself 
              with the same kindness you'd offer a good friend. Research shows it reduces 
              anxiety, depression, and self-criticism while increasing motivation and resilience.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Three Components */}
            <div className="grid md:grid-cols-3 gap-3">
              {Object.entries(SELF_COMPASSION_COMPONENTS).map(([key, component]) => (
                <div key={key} className="p-3 rounded-lg bg-background/50 border">
                  <div className="flex items-center gap-2 mb-2">
                    {component.icon}
                    <span className="font-medium text-sm">{component.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{component.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Exercise Options */}
        <div className="space-y-3">
          <h3 className="font-semibold">Choose an Exercise</h3>
          {EXERCISES.map(exercise => (
            <Card 
              key={exercise.id}
              className="cursor-pointer hover:border-secondary/30 hover:bg-secondary/5 transition-all"
              onClick={() => startExercise(exercise)}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      {exercise.title}
                      <Badge variant="outline" className="text-xs">
                        {exercise.difficulty}
                      </Badge>
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">{exercise.description}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Duration: {exercise.duration}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Phrases */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Quick Self-Compassion Phrases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {COMPASSION_PHRASES.map((phrase, i) => (
                <Badge key={i} variant="secondary" className="cursor-default">
                  {phrase}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion View
  if (isComplete) {
    return (
        <Card className="border-accent/30 bg-gradient-to-br from-accent/5 to-primary/5">
        <CardContent className="pt-6 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
              <Heart className="w-8 h-8 text-accent" />
            </div>
          </motion.div>
          <h3 className="text-xl font-bold mb-2">Well Done</h3>
          <p className="text-muted-foreground mb-4">
            You've completed a self-compassion practice. Remember, self-compassion is a skill 
            that grows with practice. Be patient and gentle with yourself.
          </p>

          {/* Summary */}
          <div className="text-left mt-6 space-y-3">
            {selectedExercise.steps.map((step, index) => (
              responses[index] && (
                <div key={index} className="p-3 rounded-lg bg-muted/50 border">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {SELF_COMPASSION_COMPONENTS[step.component as keyof typeof SELF_COMPASSION_COMPONENTS].title}
                  </p>
                  <p className="text-sm">{responses[index]}</p>
                </div>
              )
            ))}
          </div>

          <div className="flex items-center justify-center gap-2 mt-6">
            <Award className="w-5 h-5 text-secondary" />
            <span className="text-sm text-muted-foreground">+15 XP for self-compassion practice</span>
          </div>

          <Button onClick={resetExercise} className="mt-4">
            Try Another Exercise
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Exercise Step View
  const step = selectedExercise.steps[currentStep];
  const componentData = SELF_COMPASSION_COMPONENTS[step.component as keyof typeof SELF_COMPASSION_COMPONENTS];
  const progress = ((currentStep + 1) / selectedExercise.steps.length) * 100;

  return (
    <Card className="border-pink-500/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="flex items-center gap-1">
            {componentData.icon}
            {componentData.title}
          </Badge>
          <Button variant="ghost" size="sm" onClick={resetExercise}>
            Exit
          </Button>
        </div>
        <Progress value={progress} className="h-2" />
        <p className="text-xs text-muted-foreground mt-2">
          Step {currentStep + 1} of {selectedExercise.steps.length}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            {/* Step Header */}
            <div>
              <h3 className="font-semibold text-lg">{step.prompt}</h3>
              <p className="text-sm text-muted-foreground mt-1">{step.instruction}</p>
            </div>

            {/* Input */}
            <Textarea
              placeholder="Write your response here..."
              value={responses[currentStep] || ''}
              onChange={(e) => updateResponse(currentStep, e.target.value)}
              className="min-h-[150px] border-pink-500/30 focus:border-pink-500"
            />

            {/* Example */}
            <div className="p-3 rounded-lg bg-muted/50 border">
              <p className="text-xs font-medium mb-1">Example:</p>
              <p className="text-sm text-muted-foreground italic">"{step.example}"</p>
            </div>

            {/* Helpful Phrases Toggle */}
            {step.component === 'selfKindness' && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPhrases(!showPhrases)}
                  className="text-xs"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {showPhrases ? 'Hide' : 'Show'} compassionate phrases
                </Button>
                
                <AnimatePresence>
                  {showPhrases && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-wrap gap-1 mt-2">
                        {COMPASSION_PHRASES.map((phrase, i) => (
                          <Badge 
                            key={i} 
                            variant="secondary" 
                            className="text-xs cursor-pointer hover:bg-pink-500/20"
                            onClick={() => updateResponse(
                              currentStep, 
                              (responses[currentStep] || '') + (responses[currentStep] ? ' ' : '') + phrase + '.'
                            )}
                          >
                            {phrase}
                          </Badge>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!responses[currentStep]?.trim()}
                className="flex-1 bg-pink-500 hover:bg-pink-600"
              >
                {currentStep === selectedExercise.steps.length - 1 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Complete
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
